/**
 * The writes for ADR-0016's two master lists — J34…J36 (`/products`) and J38…J40
 * (`/customers`) — that `api/queries.ts` does not provide.
 *
 * WHY ONLY THE WRITES. `qk.jobMaster.list(kind, f)` already owns the READ (J33 / J37), and the
 * day sheet's picker reads the same key; declaring a second one here would give one endpoint
 * two cache slots. The same division `agents/-agents-api.ts` makes for `qk.agents.list`.
 *
 * WHY ONE MODULE FOR BOTH. The two lists are identical on the wire — `JobMasterDto` either
 * way, `JobMasterKind` naming which — and the API serves them from one service. Two copies of
 * this file would drift the first time a status code changed on one of them.
 *
 * ── PERMISSIONS ─────────────────────────────────────────────────────────────
 * `job.read` reads · `job.write` writes, transcribed from `job-master.controllers.ts`. No new
 * permission: the clerk keying the sheet is the one who meets a new customer.
 */
import type { QueryClient } from '@tanstack/react-query';
import type {
  CreateJobMasterRequest,
  JobMasterDto,
  JobMasterKind,
  UpdateJobMasterRequest,
} from '@cropmate/shared';
import { api } from '@/api/client';

const PATH: Record<JobMasterKind, string> = { product: '/products', customer: '/customers' };

/**
 * Everything the screen says that differs by kind. Kept beside the paths so a third list
 * (if one ever comes) is one entry here and one thin route file, not a fork of the screen.
 */
export const JOB_MASTER_COPY: Record<
  JobMasterKind,
  { title: string; singular: string; subtitle: string; placeholder: string }
> = {
  product: {
    title: 'Products',
    singular: 'product',
    subtitle:
      'What was handled on a job line — the workbook’s PRODUCTS column. A label only: a product never changes what a line pays.',
    placeholder: 'NPK 15-15-15',
  },
  customer: {
    title: 'Customers',
    singular: 'customer',
    subtitle:
      'Who a job line was done for — the workbook’s CUSTOMER column. A label only: a customer never changes what a line pays.',
    placeholder: 'Ladang Sri Mewah Sdn Bhd',
  },
};

export const jobMasterApi = {
  /** J34 / J38 — `job.write`. 409 `E_CONFLICT_DUPLICATE` on a name already taken,
   *  CASE-INSENSITIVELY and retired rows included, with `meta.existingId` and
   *  `meta.existingIsActive` so the screen can offer to reactivate the old row. */
  create: (kind: JobMasterKind, body: CreateJobMasterRequest) =>
    api.post<JobMasterDto>(PATH[kind], body),

  /** J35 / J39 — `job.write`. PATCH: an absent field is left unchanged (§1.2).
   *  `isActive: false` retires, `true` reactivates. */
  update: (kind: JobMasterKind, id: string, body: UpdateJobMasterRequest) =>
    api.patch<JobMasterDto>(`${PATH[kind]}/${id}`, body),

  /**
   * J36 / J40 — `job.write`, 204. REFUSED with 409 `E_FK_VIOLATION` (a sentence that says
   * "retire it instead", plus `meta.lineCount`) while any job line in any period names the
   * row: deleting it would rewrite what a paid line says it was.
   */
  remove: (kind: JobMasterKind, id: string) => api.del(`${PATH[kind]}/${id}`),
};

/**
 * A master-list write moves more than this screen.
 *
 * `['jobLines']` because the Job Lines search embeds `{ id, name, isActive }` for both refs,
 * so a rename or a retirement would otherwise print the old state there for its 30 seconds.
 * `['job-day']` (J3, the live sheet) for the same reason: its lines carry the refs too, and a
 * sheet open in another tab should not keep showing a name nobody uses any more. The other
 * kind's cache is deliberately left alone — that is why the key carries the kind.
 */
export async function invalidateJobMaster(qc: QueryClient, kind: JobMasterKind): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: ['jobMaster', kind] }),
    qc.invalidateQueries({ queryKey: ['jobLines'] }),
    qc.invalidateQueries({ queryKey: ['job-day'] }),
  ]);
}
